/**
 * Text direction for chat replies that mix Arabic and English.
 *
 * Direction comes from the share of Arabic WORDS in the prose, not from the
 * first strong character: `dir="auto"` (first-strong) lays out
 * "QA-MULTI View — كيانها …" left-to-right because it happens to start with
 * English. Nor from a letter count: an Arabic sentence carrying an email, a URL
 * or a long quoted English title has more Latin letters than Arabic ones, and
 * a letter count flipped exactly those TIA replies back to ltr.
 *
 * The rule (the shape of Closure's goog.i18n.bidi.estimateDirection):
 * - leave out what is isolated whole anyway: URLs, emails, quoted spans, and
 *   (at the markdown level) inline code, code blocks and link labels;
 * - a word with an Arabic-script letter is an Arabic word, one with a Latin
 *   letter a Latin word;
 * - rtl when Arabic words are at least 40% of the counted words, ltr when any
 *   Latin word was counted, nothing when no word was counted.
 */

export type TextDirection = 'rtl' | 'ltr';

/** Rtl from this share of Arabic words. */
const RTL_SHARE = 0.4;

/** Fewer counted words than this is no majority: the block follows the reply. */
const MIN_WORDS = 3;

/** The Arabic-script blocks: Arabic, Supplement, Extended-A, Presentation Forms A and B. */
const ARABIC_BLOCKS: ReadonlyArray<readonly [number, number]> = [
  [0x0600, 0x06ff],
  [0x0750, 0x077f],
  [0x08a0, 0x08ff],
  [0xfb50, 0xfdff],
  [0xfe70, 0xfeff],
];

/** Latin letters: Basic Latin, Latin-1, Latin Extended-A and B. */
const LATIN_LETTER = /[A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F]/u;
const LETTER = /\p{L}/u;

const URL = /\b(?:https?:\/\/|www\.)[^\s<>"]+/giu;
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+/gu;
const QUOTED = /"[^"\n]*"|\u201C[^\u201D\n]*\u201D|\u00AB[^\u00BB\n]*\u00BB/gu;

const FENCED_CODE =
  /^ *(`{3,}|~{3,})[^\n]*\n[\s\S]*?(?:^ *\1[^\n]*$|(?![\s\S]))/gmu;
const INLINE_CODE = /`[^`\n]*`/gu;
const MD_LINK = /!?\[[^\]\n]*\]\([^)\s]*\)/gu;
const AUTOLINK = /<[^>\s]+>/gu;

function isArabicLetter(char: string): boolean {
  if (!LETTER.test(char)) return false; // digits (incl. Arabic-Indic), marks, punctuation
  const code = char.codePointAt(0) ?? 0;
  return ARABIC_BLOCKS.some(([from, to]) => code >= from && code <= to);
}

function hasLatinLetters(text: string): boolean {
  return LATIN_LETTER.test(text);
}

/** Whether the text holds any Arabic-script letter at all. */
export function hasArabicLetters(text: string): boolean {
  for (const char of text) if (isArabicLetter(char)) return true;
  return false;
}

interface WordCounts {
  arabic: number;
  latin: number;
}

function countWords(text: string): WordCounts {
  let arabic = 0;
  let latin = 0;
  for (const word of text.split(/\s+/u)) {
    if (hasArabicLetters(word)) arabic += 1;
    else if (hasLatinLetters(word)) latin += 1;
  }
  return { arabic, latin };
}

function directionOf({ arabic, latin }: WordCounts): TextDirection | undefined {
  const total = arabic + latin;
  if (total === 0) return undefined;
  return arabic / total >= RTL_SHARE ? 'rtl' : 'ltr';
}

/** The text with URLs, emails and quoted spans blanked out. */
function proseOnly(text: string): string {
  return text.replace(URL, ' ').replace(EMAIL, ' ').replace(QUOTED, ' ');
}

/**
 * The direction of plain text by the word rule: rtl at 40% Arabic words,
 * ltr when Latin words win, `undefined` when no word counts. URLs, emails and
 * quoted spans are left out; Arabic-Indic digits are not letters.
 */
export function textDirection(text: string): TextDirection | undefined {
  return directionOf(countWords(proseOnly(text)));
}

/**
 * The direction of a whole markdown reply, from its FINAL content: the prior
 * a block follows when it has no majority of its own, or while it is still
 * typing out. Code, code blocks and link labels are left out with the rest.
 */
export function replyDirection(markdown: string): TextDirection | undefined {
  return textDirection(
    markdown
      .replace(FENCED_CODE, ' ')
      .replace(INLINE_CODE, ' ')
      .replace(MD_LINK, ' ')
      .replace(AUTOLINK, ' ')
  );
}

/**
 * One block's direction.
 * - `text` is all of its text; `prose` is that text without link labels and
 *   code (URLs, emails and quoted spans are left out here).
 * - Three or more counted words decide for themselves.
 * - No counted word, but English in what was left out (an email cell, a bare
 *   link): ltr.
 * - Otherwise (one or two words, or nothing at all) it follows the reply.
 */
export function blockDirection(
  text: string,
  prose: string,
  reply: TextDirection | undefined
): TextDirection | undefined {
  const counts = countWords(proseOnly(prose));
  const total = counts.arabic + counts.latin;
  if (total >= MIN_WORDS) return directionOf(counts);
  if (total === 0 && !hasArabicLetters(text) && hasLatinLetters(text))
    return 'ltr';
  return reply ?? directionOf(counts);
}

/** Whether a link or code span is an English unit, to isolate whole. */
export function isLtrUnit(text: string): boolean {
  if (!hasArabicLetters(text)) return /[A-Za-z0-9]/u.test(text);
  return directionOf(countWords(text)) === 'ltr';
}

/**
 * One LTR run: starts and ends on a Latin letter or an ASCII digit, and may
 * carry spaces and the punctuation that joins identifiers, times, emails and
 * URLs in between — so "QA-MULTI Lead A", "rank = HOT", "17:30 UTC",
 * "ada@example.com" and "https://x.test/a?b=c" are each one run. Quotes,
 * brackets and sentence-final punctuation stay outside, with the Arabic.
 */
const LTR_EDGE = 'A-Za-z0-9\\u00C0-\\u00D6\\u00D8-\\u00F6\\u00F8-\\u024F';
const LTR_RUN = new RegExp(
  `[${LTR_EDGE}](?:[${LTR_EDGE} \\t\\-_.:/@=+&%#?~,']*[${LTR_EDGE}])?`,
  'gu'
);

export interface TextSegment {
  text: string;
  /** True for a run that must be isolated left-to-right inside RTL text. */
  ltr: boolean;
}

/** Splits text into LTR runs and the text between them; joining them gives the text back. */
export function splitLtrRuns(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(LTR_RUN)) {
    const start = match.index ?? 0;
    if (start > last)
      segments.push({ text: text.slice(last, start), ltr: false });
    segments.push({ text: match[0], ltr: true });
    last = start + match[0].length;
  }
  if (last < text.length) segments.push({ text: text.slice(last), ltr: false });
  return segments;
}
